import { useState, useCallback } from 'react'
import { Plus, FolderOpen, Pencil } from 'lucide-react'
import { BrainstormChat } from './BrainstormChat'
import { DocumentPanel } from './DocumentPanel'
import { ResizableDivider } from './ResizableDivider'
import { NewSessionModal } from './NewSessionModal'
import { RenameSessionModal } from './RenameSessionModal'
import { useInterviewStore } from '@/stores/useInterviewStore'

const MIN_PANEL_WIDTH = 300
const MAX_PANEL_WIDTH = 800
const DEFAULT_PANEL_WIDTH = 500

export function BrainstormView() {
  const {
    sessionName,
    createNewSession,
    renameSession,
    openSessionPicker,
  } = useInterviewStore()

  const [documentWidth, setDocumentWidth] = useState(DEFAULT_PANEL_WIDTH)
  const [showNewSessionModal, setShowNewSessionModal] = useState(false)
  const [showRenameModal, setShowRenameModal] = useState(false)

  const handleResize = useCallback((delta: number) => {
    setDocumentWidth(prev => {
      // Delta is positive when moving right (shrinking panel), negative when moving left (growing panel)
      const newWidth = prev - delta
      return Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, newWidth))
    })
  }, [])

  const handleCreateSession = (name: string) => {
    createNewSession(name)
    setShowNewSessionModal(false)
  }

  const handleRenameSession = (name: string) => {
    renameSession(name)
    setShowRenameModal(false)
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header - matches AppShell pattern */}
      <div className="flex-shrink-0 py-3">
        <div className="max-w-6xl mx-auto px-6 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-sm font-medium text-foreground">{sessionName}</h1>
            <button
              onClick={() => setShowRenameModal(true)}
              className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title="Rename session"
            >
              <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={openSessionPicker}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title="Open sessions"
            >
              <FolderOpen className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              onClick={() => setShowNewSessionModal(true)}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title="New session"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      {/* Main content - dual pane */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full max-w-6xl mx-auto px-6 md:px-8 flex">
        {/* Chat pane */}
        <div className="flex-1 overflow-hidden">
          <BrainstormChat />
        </div>

        <ResizableDivider onResize={handleResize} />

        {/* Document pane */}
        <div
          className="flex-shrink-0 overflow-hidden"
          style={{ width: documentWidth }}
        >
          <DocumentPanel />
        </div>
        </div>
      </div>

      {/* New Session Modal */}
      <NewSessionModal
        isOpen={showNewSessionModal}
        onClose={() => setShowNewSessionModal(false)}
        onSubmit={handleCreateSession}
      />

      {/* Rename Session Modal */}
      <RenameSessionModal
        isOpen={showRenameModal}
        currentName={sessionName}
        onClose={() => setShowRenameModal(false)}
        onSubmit={handleRenameSession}
      />
    </div>
  )
}
